import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";

export default function TaskTemplateForm({ template, onClose }) {
  const [formData, setFormData] = useState({
    name: template?.name || "",
    description: template?.description || "",
    phase_name: template?.phase_name || "",
    tasks: template?.tasks || [],
    is_active: template?.is_active !== false,
  });

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.TaskTemplate.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taskTemplates"] });
      toast.success("Mal opprettet");
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.TaskTemplate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taskTemplates"] });
      toast.success("Mal oppdatert");
      onClose();
    },
  });

  const handleAddTask = () => {
    setFormData({
      ...formData,
      tasks: [
        ...formData.tasks,
        {
          title: "",
          description: "",
          priority: "medium",
          estimated_hours: 0,
          order: formData.tasks.length,
        },
      ],
    });
  };

  const handleUpdateTask = (index, field, value) => {
    const newTasks = [...formData.tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setFormData({ ...formData, tasks: newTasks });
  };

  const handleRemoveTask = (index) => {
    setFormData({
      ...formData,
      tasks: formData.tasks.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || formData.tasks.length === 0) {
      toast.error("Fyll inn navn og legg til minst én oppgave");
      return;
    }

    if (template) {
      updateMutation.mutate({ id: template.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">
            {template ? "Rediger Mal" : "Opprett Ny Mal"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Malnavn *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="f.eks. Standard Prosjektoppstart"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              placeholder="Beskriv hva denne malen brukes til"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phase_name">Fasenavn (Valgfritt)</Label>
            <Input
              id="phase_name"
              value={formData.phase_name}
              onChange={(e) => setFormData({ ...formData, phase_name: e.target.value })}
              placeholder="f.eks. Oppstart"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Oppgaver i mal *</Label>
              <Button type="button" onClick={handleAddTask} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Legg til oppgave
              </Button>
            </div>

            {formData.tasks.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500 mb-3">Ingen oppgaver lagt til</p>
                <Button type="button" onClick={handleAddTask} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Legg til første oppgave
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.tasks.map((task, index) => (
                  <Card key={index} className="p-4 bg-slate-50">
                    <div className="flex items-start gap-3">
                      <GripVertical className="w-4 h-4 text-slate-400 mt-2" />
                      <div className="flex-1 space-y-3">
                        <Input
                          value={task.title}
                          onChange={(e) => handleUpdateTask(index, "title", e.target.value)}
                          placeholder="Oppgavetittel"
                          required
                        />
                        <Textarea
                          value={task.description}
                          onChange={(e) => handleUpdateTask(index, "description", e.target.value)}
                          placeholder="Beskrivelse (valgfritt)"
                          rows={2}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Select
                            value={task.priority}
                            onValueChange={(v) => handleUpdateTask(index, "priority", v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Lav prioritet</SelectItem>
                              <SelectItem value="medium">Middels prioritet</SelectItem>
                              <SelectItem value="high">Høy prioritet</SelectItem>
                              <SelectItem value="urgent">Haster</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            value={task.estimated_hours || ""}
                            onChange={(e) => handleUpdateTask(index, "estimated_hours", parseFloat(e.target.value) || 0)}
                            placeholder="Timer"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveTask(index)}
                      >
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="bg-zinc-900 hover:bg-zinc-900"
            >
              {template ? "Oppdater Mal" : "Opprett Mal"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}